/**
 * useMonthlyRollover — trigger idempotente del reset mensile.
 *
 * Chiama:
 *   1. `POST /api/monthly/force-reset-2026-08` una sola volta per lo sprint
 *      di transizione (idempotente lato server: no-op se già effettuato).
 *   2. `POST /api/monthly/rollover` a ogni boot autenticato (idempotente).
 *
 * Dopo la chiamata, invoca `onDone()` per far ricaricare hooks come
 * `useTimeWatchData`, `useImpronte`, `useIstanti`, `useBackups`.
 */
import { useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Marker LS scoped-user per evitare di rifare la force-reset ad ogni boot.
// Il backend è comunque idempotente, questo evita solo la richiesta HTTP.
function lsKey(userId) {
  return `time-inside:monthly:force2026-08:user:${userId}`;
}

export function useMonthlyRollover(onDone) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const doneRef = useRef(false);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user?.id) return;
    if (doneRef.current) return;
    doneRef.current = true;
    (async () => {
      const uid = user.id;
      try {
        // Force one-shot solo se non ancora eseguita per questo utente su
        // questo device (best-effort ottimizzazione; backend idempotente).
        let alreadyForced = false;
        try {
          alreadyForced =
            window.localStorage.getItem(lsKey(uid)) === "1";
        } catch { /* ignore */ }
        if (!alreadyForced) {
          try {
            await axios.post(`${API}/monthly/force-reset-2026-08`);
          } catch { /* ignore */ }
          try {
            window.localStorage.setItem(lsKey(uid), "1");
          } catch { /* ignore */ }
          try {
            // Notifica gli hooks (useTimeWatchData/useImpronte/useIstanti/
            // useBackups) affinché ricarichino i dati dopo la force-reset.
            window.dispatchEvent(new CustomEvent("ti:rollover-done"));
          } catch { /* ignore */ }
        }
        // Rollover regolare (idempotente)
        try {
          const r = await axios.post(`${API}/monthly/rollover`);
          if (r?.data?.changed) {
            try {
              window.dispatchEvent(new CustomEvent("ti:rollover-done"));
            } catch { /* ignore */ }
          }
        } catch { /* ignore */ }
      } finally {
        if (typeof onDone === "function") onDone();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading, user?.id]);
}
